/* LittleStars autocomplete: native suggestions plus read-only RetailSync SKU matches. */
(() => {
  // The header can be rendered more than once by Shopify's theme editor.
  if (window.littlestarsSearchInitialized) return;
  window.littlestarsSearchInitialized = true;
  const escapeHTML = value => String(value ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  const skuIndexes = new Map();
  function loadSkuIndex(prefix) {
    if (skuIndexes.has(prefix)) return skuIndexes.get(prefix);
    const promise = (async () => {
      const readPage = async page => {
        const params = new URLSearchParams({view: 'littlestars-sku-index', page: String(page), sort_by: 'title-ascending'});
        const response = await fetch(prefix + '/collections/all?' + params, {signal: AbortSignal.timeout(15000)});
        if (!response.ok) throw new Error('SKU index unavailable');
        const data = await response.json();
        // Missing alternate templates can return the normal collection HTML.
        if (data.version !== 1 || data.page !== page || !Array.isArray(data.products)
          || !Number.isInteger(data.pages) || data.pages < 0 || data.pages > 100
          || !Number.isInteger(data.total) || data.total > 25000) throw new Error('Invalid SKU index');
        return data;
      };
      const first = await readPage(1);
      const pages = [first.products];
      let nextPage = 2;
      // Limit traffic: up to three paginated requests at a time, once per page visit.
      await Promise.all(Array.from({length: Math.min(3, first.pages - 1)}, async () => {
        while (nextPage <= first.pages) {
          const page = nextPage++;
          const data = await readPage(page);
          if (data.pages !== first.pages || data.total !== first.total) throw new Error('SKU index changed; retry');
          pages[page - 1] = data.products;
        }
      }));
      return [...new Map(pages.flat().filter(p => typeof p.system_sku === 'string' && typeof p.url === 'string')
        .map(p => [String(p.id), p])).values()];
    })();
    skuIndexes.set(prefix, promise);
    // A later search can retry a failed request (for example after uploading the helper).
    promise.catch(() => { if (skuIndexes.get(prefix) === promise) skuIndexes.delete(prefix); });
    return promise;
  }
  function matchingSkus(index, query) {
    const term = query.trim().toLocaleLowerCase();
    // RetailSync can store several codes in one comma-separated metafield.
    // Match each code separately while returning each product only once.
    return index.map(product => ({
      product,
      codes: product.system_sku.split(',').map(code => code.trim().toLocaleLowerCase()).filter(Boolean)
    })).filter(entry => entry.codes.some(code => code.startsWith(term))).sort((a, b) =>
      Number(b.codes.includes(term)) - Number(a.codes.includes(term))
      || Number(b.product.available) - Number(a.product.available)
      || a.product.title.localeCompare(b.product.title)
    ).slice(0, 9).map(entry => entry.product);
  }
  function mergeProducts(skuProducts, nativeProducts) {
    const seen = new Set();
    const nativeById = new Map(nativeProducts.map(product => [String(product.id), product]));
    const matches = skuProducts.map(product => ({...nativeById.get(String(product.id)), ...product}));
    return [...matches, ...nativeProducts].filter(product => {
      const key = product.id ? String(product.id) : product.url.split('?')[0];
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }).slice(0, 9);
  }
  // Check only suggested collections, not the whole catalogue. Cache verified results.
  const collectionHasProducts = new Map();
  async function nonEmptyCollection(collection, prefix, signal) {
    const handle = collection.handle || (collection.url || '').split('/collections/')[1]?.split(/[?#/]/)[0];
    if (!handle) return true; // Unknown: do not hide an unverified collection.
    if (collectionHasProducts.has(handle)) return collectionHasProducts.get(handle);
    try {
      const response = await fetch(prefix + '/collections/' + encodeURIComponent(handle) + '/products.json?limit=1', {signal});
      if (!response.ok) return true;
      const data = await response.json();
      if (!Array.isArray(data.products)) return true;
      const hasProducts = data.products.length > 0;
      collectionHasProducts.set(handle, hasProducts);
      return hasProducts;
    } catch (error) {
      if (error.name === 'AbortError') throw error;
      return true; // Network failure must not silently hide valid collections.
    }
  }
  function init(root = document) {
    root.querySelectorAll('form[data-ls-search]:not([data-ls-ready])').forEach(form => {
      form.dataset.lsReady = '1';
      const input = form.querySelector('input[name="q"]');
      if (!input) return;
      const prefix = (form.dataset.root || '/').replace(/\/$/, '') || '';
      let panel, timer, controller, serial = 0;
      let resetButton;
      const close = () => {
        if (panel) { panel.remove(); panel = null; }
        resetButton?.remove(); resetButton = null;
        form.classList.remove('ls-search-open');
        input.setAttribute('aria-expanded', 'false');
        input.removeAttribute('aria-controls');
      };
      const dismiss = () => {
        clearTimeout(timer);
        controller?.abort();
        ++serial;
        close();
      };
      const reset = () => {
        clearTimeout(timer);
        controller?.abort();
        ++serial;
        input.value = '';
        close();
        input.focus();
      };
      const render = (data, query, collectionsOverride, skuProducts = [], skuStatus = '') => {
        const focusedHref = panel?.contains(document.activeElement)
          ? document.activeElement.closest('a')?.getAttribute('href') : null;
        close();
        if (input.value.trim() !== query) return;
        const resources = data.resources?.results || {};
        const products = mergeProducts(skuProducts, resources.products || []);
        const collections = collectionsOverride ?? resources.collections ?? [];
        // Tag suggestions come from matching products only; Shopify does not provide a global tag search.
        const seenTags = new Set();
        const tags = [];
        for (const product of products) {
          for (const rawTag of (Array.isArray(product.tags) ? product.tags : [])) {
            const tag = String(rawTag).trim();
            const key = tag.toLocaleLowerCase();
            if (tag && !seenTags.has(key)) { seenTags.add(key); tags.push(tag); }
          }
        }
        tags.sort((a, b) => a.localeCompare(b));
        panel = document.createElement('div');
        panel.className = 'ls-search-results';
        panel.id = 'ls-search-' + Math.random().toString(36).slice(2);
        panel.setAttribute('role', 'region');
        panel.setAttribute('aria-label', 'Search suggestions');
        const group = (title, items, format) => items.length ? `<div class="ls-search-group${title === 'Including Tags' ? ' ls-search-tags' : ''}"><strong>${title}</strong><ul>${items.map(format).join('')}</ul></div>` : '';
        panel.innerHTML = group('Suggested Products', products, p => `<li><a href="${escapeHTML(p.url)}">${p.featured_image?.url ? `<img src="${escapeHTML(p.featured_image.url)}" alt="" loading="lazy" width="44" height="44">` : ''}<span>${escapeHTML(p.title)}</span></a></li>`) +
          group('Including Tags', tags, tag => `<li><a href="${escapeHTML(prefix + '/collections/all/' + encodeURIComponent(tag.toLowerCase().replace(/\s+/g, '-')))}">${escapeHTML(tag)}</a></li>`) +
          group('Containing Collections', collections, c => `<li><a href="${escapeHTML(c.url)}">${escapeHTML(c.title.replace(/^Custom\s*-\s*/i, ''))}</a></li>`) +
          (skuStatus ? `<p class="ls-search-empty" role="status">${escapeHTML(skuStatus)}</p>` : '') +
          (!products.length && !tags.length && !collections.length && !skuStatus ? '<p class="ls-search-empty">No results found</p>' : '') +
          `<div class="ls-search-all-wrap"><a class="ls-search-all button button--primary" href="${escapeHTML(prefix + '/search?q=' + encodeURIComponent(query))}">View all results</a></div>`;
        form.appendChild(panel);
        form.classList.add('ls-search-open');
        resetButton = document.createElement('button');
        resetButton.type = 'button';
        resetButton.className = 'ls-search-reset';
        resetButton.setAttribute('aria-label', 'Clear search');
        resetButton.textContent = '×';
        resetButton.addEventListener('click', reset);
        form.insertBefore(resetButton, form.querySelector('button[type="submit"]'));
        input.setAttribute('aria-controls', panel.id);
        input.setAttribute('aria-expanded', 'true');
        if (focusedHref) {
          ([...panel.querySelectorAll('a')].find(link => link.getAttribute('href') === focusedHref) || input).focus();
        }
      };
      input.addEventListener('input', () => {
        clearTimeout(timer); controller?.abort(); ++serial; close();
        const query = input.value.trim();
        if (query.length < 2) return;
        const current = serial;
        timer = setTimeout(async () => {
          controller = new AbortController();
          const signal = controller.signal;
          let data = {}, visibleCollections = [], skuProducts = [];
          let skuStatus = 'Loading system SKU matches…';
          const refresh = () => {
            if (current === serial && document.contains(form)) render(data, query, visibleCollections, skuProducts, skuStatus);
          };
          refresh();
          // Keep native autocomplete responsive while the index loads. The index
          // also supports letter-only codes, and is shared across search inputs.
          loadSkuIndex(prefix).then(index => {
            skuProducts = matchingSkus(index, query);
            skuStatus = '';
            refresh();
          }).catch(error => {
            console.warn('[LittleStars search] System SKU lookup unavailable:', error);
            skuStatus = 'System SKU lookup is temporarily unavailable.';
            refresh();
          });
          const params = new URLSearchParams({q:query, 'resources[type]':'product,collection', 'resources[limit]':'10', 'resources[options][unavailable_products]':'last'});
          try {
            const response = await fetch(prefix + '/search/suggest.json?' + params, {signal});
            if (!response.ok) throw new Error('Search unavailable');
            data = await response.json();
            refresh();
            const collections = data.resources?.results?.collections || [];
            // One lightweight request per suggested collection; no preloaded catalogue.
            visibleCollections = (await Promise.all(collections.map(async collection =>
              (await nonEmptyCollection(collection, prefix, signal)) ? collection : null
            ))).filter(Boolean);
            refresh();
          } catch (error) {
            if (error.name !== 'AbortError') {
              console.warn('[LittleStars search] Native suggestions unavailable:', error);
              refresh();
            }
          }
        }, 250);
      });
      input.addEventListener('keydown', event => {
        if (event.key === 'Escape') { dismiss(); return; }
        if (event.key === 'ArrowDown' && panel) { const link = panel.querySelector('a'); if (link) { event.preventDefault(); event.stopPropagation(); link.focus(); } }
      });
      form.addEventListener('keydown', event => {
        if (!panel || !['ArrowDown','ArrowUp','Escape'].includes(event.key)) return;
        if (event.key === 'Escape') { dismiss(); input.focus(); return; }
        const links = [...panel.querySelectorAll('a')];
        const index = links.indexOf(document.activeElement);
        if (index < 0) return;
        event.preventDefault();
        const next = index + (event.key === 'ArrowDown' ? 1 : -1);
        (links[next] || input).focus();
      });
      document.addEventListener('pointerdown', event => { if (!form.contains(event.target)) dismiss(); });
      form.addEventListener('submit', dismiss);
      form.addEventListener('focusout', () => {
        queueMicrotask(() => { if (!form.contains(document.activeElement)) dismiss(); });
      });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init()); else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
